/* ==========================================================================
   FIGS footer — the one site footer every prototype shows (Figma Footer
   351:9827: Desktop 359:10312, Mobile 351:9826 / Expanded 351:9828; built
   2026-10-15, link lists, offer and accordion icons resynced 2026-10-28).

   mountFigsNav() mounts it: a `footer` placeholder passed to it becomes this
   footer, and so does any page footer a prototype was built with (the
   templates' `footer[data-footer]`, the PLPs' desktop + mobile pair), so a
   link or style change happens here once. Pages that reveal their footer
   find it by `data-footer`; this footer reveals its own rows.

   Desktop (≥ 780): the sign-up left, four link columns right; then the logo
   and the legal links, a rule, and the copyright beside the country picker.
   Mobile: the sign-up, five accordions (+ / −), then the logo beside the
   country picker, a rule and the copyright. The first four accordions are
   the desktop columns; Figma only opens The Company, so the others hold
   their column's links, and Legal holds the desktop legal row (mobile has
   no legal row of its own).
   ========================================================================== */

import './figs-footer.css';

const asset = (name) => new URL(`./assets/${name}`, import.meta.url).href;
const ICON = {
  chevron: asset('footer-chevron-right.svg'),
  // the refined (tapered) + / −, as the nav's expand icons; the plain
  // footer-plus.svg / footer-minus.svg they replaced stay in assets/
  plus: asset('footer-plus-refined.svg'),
  minus: asset('footer-minus-refined.svg'),
  logo: asset('footer-logo.svg'),
  flag: asset('footer-flag-usa.svg'),
  privacy: asset('footer-privacy-choices.png'),
};

const COLUMNS = [
  { title: 'The Company', links: ['Newsroom', 'Our Story'] },
  { title: 'Get Involved', links: ['Recycle Your Scrubs', 'Awesome Humans Foundation', 'Join Our Research Panel', 'Provide Feedback', 'Careers'] },
  { title: 'Assistance', links: ['Help Center', 'Investors', 'Shipping', 'Returns & Exchanges', 'Sign Up for Texts', 'Contact Us', 'Legal'] },
  { title: 'Gifts & Discounts', links: ['Gift Cards', 'Student Discount', 'Military Discount', 'Refer a Friend'] },
];
const LEGAL = ['Promo Terms & Conditions', 'Accessibility Statement', 'CA Privacy Notice', 'CA Transparency Act'];
const ACCORDIONS = [
  ...COLUMNS,
  { title: 'Legal', links: [...LEGAL, 'Your Privacy Choices'] },
];

const esc = (s) => s.replace(/&/g, '&amp;');
// footer links with a prototype behind them; the rest stay placeholders (#)
const FOOTER_HREFS = { Newsroom: '/experiments/19-newsroom/' };
const link = (label) => `<a href="${FOOTER_HREFS[label] || '#'}">${esc(label)}</a>`;
const locale = `<span class="fnav-site-footer-locale fnav-js-open-country-drawer">US | English <img src="${ICON.flag}" alt="" width="16" height="16"></span>`;
const terms = `<span class="fnav-site-footer-terms"><a href="#">Terms of Use</a> • <a href="#">Privacy Policy</a></span>`;
const logo = `<img class="fnav-site-footer-logo" src="${ICON.logo}" alt="FIGS" width="96" height="27">`;

let uid = 0;
export function footerHTML() {
  const id = `fnav-footer-${++uid}`;
  return `<footer class="fnav-site-footer" data-footer data-fnav-footer>
  <div class="fnav-site-footer-top">
    <div class="fnav-site-footer-signup" data-fnav-footer-row>
      <p class="fnav-site-footer-eyebrow">Awesome Humans #WearFIGS</p>
      <p class="fnav-site-footer-blurb">Sign up to receive 15% off your first order and stay up to date on color drops, new collections, events, and exclusive offers.</p>
      <a href="#" class="fnav-site-footer-cta">Sign Up<img src="${ICON.chevron}" alt="" width="10" height="10"></a>
    </div>
    <nav class="fnav-site-footer-cols" aria-label="Footer">
      ${COLUMNS.map((c) => `<div class="fnav-site-footer-col" data-fnav-footer-row>
        <p class="fnav-site-footer-heading">${esc(c.title)}</p>
        <div class="fnav-site-footer-links">${c.links.map(link).join('')}</div>
      </div>`).join('')}
    </nav>
    <nav class="fnav-site-footer-acc" aria-label="Footer">
      ${ACCORDIONS.map((g, i) => `<div class="fnav-site-footer-group" data-fnav-footer-row>
        <button class="fnav-site-footer-group-head" type="button" aria-expanded="false" aria-controls="${id}-${i}" data-fnav-footer-toggle>
          <span>${esc(g.title)}</span>
          <img class="fnav-site-footer-plus" src="${ICON.plus}" alt="" width="10" height="10"><img class="fnav-site-footer-minus" src="${ICON.minus}" alt="" width="10" height="2">
        </button>
        <div class="fnav-site-footer-links" id="${id}-${i}" hidden>${g.links.map(link).join('')}</div>
      </div>`).join('')}
    </nav>
  </div>
  <div class="fnav-site-footer-bottom fnav-site-footer-bottom--d" data-fnav-footer-row>
    <div class="fnav-site-footer-row">
      ${logo}
      <div class="fnav-site-footer-legal">
        ${LEGAL.map(link).join('')}
        <a href="#" class="fnav-site-footer-choices">Your Privacy Choices<img src="${ICON.privacy}" alt="" width="19" height="9"></a>
      </div>
    </div>
    <hr class="fnav-site-footer-rule">
    <div class="fnav-site-footer-row">
      <div class="fnav-site-footer-fine">
        <span class="fnav-site-footer-copy"><span class="fnav-site-footer-c">©</span> 2026 FIGS, Inc. All Rights Reserved</span>
        ${terms}
      </div>
      ${locale}
    </div>
  </div>
  <div class="fnav-site-footer-bottom fnav-site-footer-bottom--m" data-fnav-footer-row>
    <div class="fnav-site-footer-row">${logo}${locale}</div>
    <hr class="fnav-site-footer-rule">
    <div class="fnav-site-footer-fine">
      <span class="fnav-site-footer-copy">© 2026 FIGS, Inc. All Rights Reserved</span>
      ${terms}
    </div>
  </div>
</footer>`;
}

const fragment = (html) => document.createRange().createContextualFragment(html);
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/* the accordions (mobile): one open at a time isn't asked for, so each
   toggles on its own; + turns to − */
function bind(footer) {
  if (footer.dataset.fnavFooterBound) return;
  footer.dataset.fnavFooterBound = '1';
  footer.querySelectorAll('[data-fnav-footer-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      document.getElementById(btn.getAttribute('aria-controls')).hidden = !open;
    });
  });
  /* the rows rise in, left to right and top down, when the footer scrolls
     into view (Reduced: already there) */
  if (reduced() || !('IntersectionObserver' in window)) return;
  footer.querySelectorAll('[data-fnav-footer-row]').forEach((row, i) => row.style.setProperty('--i', i));
  footer.classList.add('is-armed');
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    footer.classList.add('is-in');
    io.disconnect();
  }, { threshold: 0.12 });
  io.observe(footer);
}

/* every page's footer becomes this one: a page footer a prototype was built
   with is replaced where it stood (the first; any second — the PLPs' mobile
   copy — goes), and every site footer is bound */
const LEGACY = 'footer[data-footer]:not([data-fnav-footer]), footer.footer--desktop, footer.footer-m';
export function mountFigsFooter(placeholder = null) {
  if (placeholder) placeholder.replaceWith(fragment(footerHTML()));
  const onPage = () => [...document.querySelectorAll('[data-fnav-footer]')].some((f) => !f.closest('.fnav-results-page'));
  document.querySelectorAll(LEGACY).forEach((f) => {
    if (onPage()) f.remove();
    else f.replaceWith(fragment(footerHTML()));
  });
  document.querySelectorAll('[data-fnav-footer]').forEach(bind);
}
